import React, { useState, useRef, useEffect } from "react";
import { InputWithMediaInfo } from "../InputWithMediaInfo/InputWithMediaInfo";
import { InputWithMediaInfoRef } from "@/database";
import { SelectedMediaCard } from "../SelectedMediaCard";
import { GeneralPlayer } from "../CleoPlayer/GeneralPlayer";
import { Button, toast } from "..";
import Modal from "../Modal/Modal";
import SelectPreset from "../MetaPresets/SelectPreset";
import { Upload } from "lucide-react";
import { useDebounce } from "../../utils";
interface BaseOverlay {
  id: string;
  url: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  visible: boolean;
  title?: string;
}

type LogoOverlay = BaseOverlay;

interface SlateOverlay extends BaseOverlay {
  isSlate: boolean;
  duration?: number;
  autoHide?: boolean;
  isPreRoll?: boolean;
  isPostRoll?: boolean;
}

interface UnifiedOverlayControlsProps {
  logoOverlays?: LogoOverlay[];
  slateOverlays?: SlateOverlay[];
  presetList?: any[];
  onLogoOverlaysChange?: (logoOverlays: LogoOverlay[]) => void;
  onSlateOverlaysChange?: (slateOverlays: SlateOverlay[]) => void;
  onLogoUpload?: (file: File) => void;
  onSlateUpload?: (file: File) => void;
  overlayType?: "logo" | "slate";
  title?: string;
  showAddButton?: boolean;
  showRemoveButton?: boolean;
  showUploadControl?: boolean;
  isPrePostRollMode?: boolean;
  onPreRollChange?: (media: any) => void;
  onPostRollChange?: (media: any) => void;
  preRollMedia?: any;
  postRollMedia?: any;
  preRollMediaList?: any[];
  postRollMediaList?: any[];
  onPreRollSearch?: (params: { stitle: string; key: string }) => void;
  onPostRollSearch?: (params: { stitle: string; key: string }) => void;
  onClearPreRollMedia?: () => void;
  onClearPostRollMedia?: () => void;
  onLogoOverlaySelect?: (logoOverlayId: string) => void; // New prop for logo selection
  selectedLogoOverlayId?: string;
  isPreset?: boolean;
  ImageUpload?: any;
  callRegisterMediaOnCms?: any;
  currentMedia?: any;
  handlePresetId?: (presetId: string) => void;
  isPreview?: boolean;
  readOnlyMode?: boolean;
}

export const UnifiedOverlayControls: React.FC<UnifiedOverlayControlsProps> = ({
  logoOverlays = [],
  slateOverlays = [],
  onLogoOverlaysChange,
  onSlateOverlaysChange,
  onLogoUpload,
  onSlateUpload,
  overlayType = "logo",
  title,
  showAddButton = true,
  isPrePostRollMode = false,
  onPreRollChange,
  onPostRollChange,
  preRollMedia,
  postRollMedia,
  preRollMediaList = [],
  postRollMediaList = [],
  onPreRollSearch,
  onPostRollSearch,
  onClearPreRollMedia,
  onClearPostRollMedia,
  onLogoOverlaySelect,
  selectedLogoOverlayId,
  isPreset = false,
  ImageUpload,
  callRegisterMediaOnCms,
  currentMedia,
  handlePresetId,
  presetList,
  isPreview,
  readOnlyMode = false,
}) => {
  const [isPreEdit, setIsPreEdit] = useState<boolean>(false);
  const [isPostEdit, setIsPostEdit] = useState<boolean>(false);
  const preMediaInputRef = useRef<InputWithMediaInfoRef>(null);
  const postMediaInputRef = useRef<InputWithMediaInfoRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const callback = useRef<((query: any, token: any) => void) | null>(null);
  const [isShowPresetModal, setIsShowPresetModal] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<{
    error: boolean;
    success: boolean;
    loading: boolean;
  }>({
    error: false,
    success: false,
    loading: false,
  });
  const [presetId, setPresetId] = useState<string | null>(null);
  const [preRollValue, setPreRollValue] = useState<string>("");
  const [postRollValue, setPostRollValue] = useState<string>("");

  const debouncedPreRollValue = useDebounce(preRollValue, 200);
  const debouncedPostRollValue = useDebounce(postRollValue, 200);

  // Refs to track last processed values to prevent infinite loops
  const lastProcessedPreRollValue = useRef<any>(null);
  const lastProcessedPostRollValue = useRef<any>(null);
  const onPreRollChangeRef = useRef(onPreRollSearch);
  const onPostRollChangeRef = useRef(onPostRollSearch);

  // Keep refs updated
  useEffect(() => {
    onPreRollChangeRef.current = onPreRollSearch;
    onPostRollChangeRef.current = onPostRollSearch;
  }, [onPreRollSearch, onPostRollSearch]);

  const getOverlayData = () => {
    switch (overlayType) {
      case "slate":
        return {
          overlays: slateOverlays,
          onChange: onSlateOverlaysChange,
          onUpload: onSlateUpload,
          defaultTitle: "Slate",
          addButtonText: "Add Slate",
          uploadLabel: "Upload Slate",
          defaultSize: { width: 200, height: 200 },
          defaultPosition: { x: 50, y: 50 },
          idPrefix: "slate",
          defaultDuration: 5,
        };
      case "logo":
        return {
          overlays: logoOverlays,
          onChange: onLogoOverlaysChange,
          onUpload: onLogoUpload,
          defaultTitle: "Graphics",
          addButtonText: "UPLOAD",
          uploadLabel: "Upload Logo",
          defaultSize: { width: 200, height: 200 },
          defaultPosition: { x: 20, y: 20 },
          idPrefix: "logo",
        };
      default:
        return {
          overlays: logoOverlays,
          onChange: onLogoOverlaysChange,
          onUpload: onLogoUpload,
          defaultTitle: "Logo",
          addButtonText: "Add Logo",
          uploadLabel: "Upload Logo",
          defaultSize: { width: 200, height: 200 },
          defaultPosition: { x: 20, y: 20 },
          idPrefix: "logo",
        };
    }
  };

  const {
    overlays,
    onChange,
    defaultTitle,
    addButtonText,
    defaultSize,
    defaultPosition,
    idPrefix,
    defaultDuration,
  } = getOverlayData();

  useEffect(() => {
    // Reset ref if value is cleared
    if (debouncedPreRollValue === null || debouncedPreRollValue === "") {
      lastProcessedPreRollValue.current = null;
      return;
    }

    // Check if it's a valid value (object/MediaOption or string with length > 3)
    const isValidValue =
      (typeof debouncedPreRollValue === "object" &&
        debouncedPreRollValue !== null) ||
      (typeof debouncedPreRollValue === "string" &&
        debouncedPreRollValue.length > 3);

    // Only call if value has actually changed to prevent infinite loops
    const valueKey =
      typeof debouncedPreRollValue === "object"
        ? JSON.stringify(debouncedPreRollValue)
        : debouncedPreRollValue;

    if (isValidValue && lastProcessedPreRollValue.current !== valueKey) {
      lastProcessedPreRollValue.current = valueKey;
      onPreRollChangeRef.current?.({
        stitle: debouncedPreRollValue,
        key: "pre",
      });
    }
  }, [debouncedPreRollValue]);

  useEffect(() => {
    // Reset ref if value is cleared
    if (debouncedPostRollValue === null || debouncedPostRollValue === "") {
      lastProcessedPostRollValue.current = null;
      return;
    }

    // Check if it's a valid value (object/MediaOption or string with length > 3)
    const isValidValue =
      (typeof debouncedPostRollValue === "object" &&
        debouncedPostRollValue !== null) ||
      (typeof debouncedPostRollValue === "string" &&
        debouncedPostRollValue.length > 3);

    // Only call if value has actually changed to prevent infinite loops
    const valueKey =
      typeof debouncedPostRollValue === "object"
        ? JSON.stringify(debouncedPostRollValue)
        : debouncedPostRollValue;

    if (isValidValue && lastProcessedPostRollValue.current !== valueKey) {
      lastProcessedPostRollValue.current = valueKey;
      onPostRollChangeRef.current?.({
        stitle: debouncedPostRollValue,
        key: "post",
      });
    }
  }, [debouncedPostRollValue]);

  const handleSelectPreset = (presetId: any) => {
    setPresetId(presetId?.id);
    setIsShowPresetModal(false);
    handlePresetId?.(presetId?.id);
  };

  const handleImageUploadUrl = (imageUrl: string) => {
    setImageUrl(imageUrl);
  };

  const handleOpenMenu = (key: string) => {
    if (key === "pre") {
      setTimeout(() => {
        preMediaInputRef.current?.openMenu();
      }, 0);
    } else {
      setTimeout(() => {
        postMediaInputRef.current?.openMenu();
      }, 0);
    }
  };

  const handleEditMediaCard = (key: string) => {
    if (key === "pre") {
      handleOpenMenu("pre");
      setIsPreEdit(true);
    } else {
      handleOpenMenu("post");
      setIsPostEdit(true);
    }
  };

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    overlayId?: string
  ) => {
    const file = event.target.files?.[0];

    if (file && onChange) {
      if (overlayType === "logo" && onLogoUpload && !overlayId) {
        onLogoUpload(file);
        if (event.target) {
          event.target.value = "";
        }
        return;
      }

      if (overlayId) {
        const updatedOverlays = overlays.map((overlay: BaseOverlay) =>
          overlay.id === overlayId
            ? { ...overlay, url: URL.createObjectURL(file) }
            : overlay
        );

        onChange(updatedOverlays as any);
      } else {
        const newOverlay: any = {
          id: `${idPrefix}-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
          url: URL.createObjectURL(file),
          position: defaultPosition,
          size: defaultSize,
          visible: true,
        };
        if (overlayType === "slate") {
          newOverlay.isSlate = true;
          newOverlay.duration = defaultDuration;
          newOverlay.autoHide = false;
        }

        onChange([...overlays, newOverlay] as any);
      }
    }
  };

  const uploadLogo = (id: string, pList: any) => {
    // const productId = currentMedia ? currentMedia?.id : id;
    if (selectedImage && callback.current != null) {
      // setIsLoading({ error: false, success: false, loading: true });
      callRegisterMediaOnCms(selectedImage, (response: any) => {
        if (response && response.status === 200) {
          const token = response.data.jwt;
          const query = {
            entity: currentMedia?.id,
            key: "logo",
          };
          callback.current && callback.current(query, token);

          const updatedProduct = pList.filter(
            (product: any) => product.id === currentMedia?.id
          );
          const ub = { ...updatedProduct[0], logo: response.data.AssetID };
          // setIsLoading({ error: false, success: false, loading: false });
        } else {
          toast.fail("");
        }
      });
    } else {
      // setIsLoading({ error: false, success: false, loading: false });
    }
  };

  useEffect(() => {
    if (imageUrl) {
      const newOverlay: any = {
        id: `${idPrefix}-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        url: imageUrl,
        position: defaultPosition,
        size: defaultSize,
        visible: true,
      };
      onChange && onChange([...overlays, newOverlay] as any);
    }
  }, [imageUrl]);

  const addOverlay = () => {
    if (!onChange) return;

    if (overlayType === "logo") {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    const newOverlay: any = {
      id: `${idPrefix}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      url: "",
      position: defaultPosition,
      size: defaultSize,
      visible: true,
    };

    if (overlayType === "slate") {
      newOverlay.isSlate = true;
      newOverlay.duration = defaultDuration;
      newOverlay.autoHide = false;
    }

    onChange([...overlays, newOverlay] as any);
  };

  useEffect(() => {
    if (isPreset) {
      if (selectedImage) {
        uploadLogo(currentMedia?.id, overlays);
      }
    }
  }, [isPreset, selectedImage]);

  if (!onChange) {
    return null;
  }

  if (overlayType === "slate" && isPrePostRollMode) {
    const preRollSlate = slateOverlays.find(
      (slate) => slate?.isPreRoll && slate?.visible
    );
    const postRollSlate = slateOverlays.find(
      (slate) => slate?.isPostRoll && slate?.visible
    );

    return (
      <div className="pb-3">
        {isShowPresetModal && (
          <Modal
            isOpen={isShowPresetModal}
            title="Preset"
            onClose={() => setIsShowPresetModal(false)}
            width="1000px"
            height="80%"
          >
            <SelectPreset
              type="branding"
              handleSelectPreset={handleSelectPreset}
              isWizard={false}
            />
          </Modal>
        )}
        {!isPreset && (
          <div className="border-bottom pb-3 mb-3">
            <h6 className="fw-medium text-primary text-uppercase">Preset</h6>
            <small className="text-secondary d-block">
              <i>Cut down repetitive work. Apply Presets to your uploads.</i>
            </small>
            <Button
              type="outline-primary"
              className="border mt-2"
              isDisabled={presetList?.length === 0}
              onClick={() => setIsShowPresetModal(true)}
            >
              Select
            </Button>
          </div>
        )}
        <h6 className="text-uppercase d-flex">
          {title || "Slates"}
          <div className="d-flex ml-auto H">
            <button className="btn btn-sm btn-outline-secondary">
              Landscape
            </button>
            <button className="btn btn-sm btn-outline-secondary">
              Vertical
            </button>
          </div>
        </h6>
        <div className="row gy-3">
          <div className="col-12">
            {preRollSlate && !isPreEdit ? (
              <div>
                <label className="form-label mb-1">Opening Slate</label>
                <SelectedMediaCard
                  media={{
                    label: preRollSlate.id,
                    value: preRollSlate.title || "Pre-Roll Slate",
                    image: preRollSlate.url,
                    duration: `${preRollSlate.duration || 5}s`,
                    thumb: !!preRollSlate.url,
                  }}
                  type="pre"
                  showEditButton={true}
                  showPreviewButton={true}
                  disableEdit={isPreview}
                  onEdit={() => handleEditMediaCard("pre")}
                  readOnlyMode={readOnlyMode}
                  onClose={() => {
                    if (onSlateOverlaysChange) {
                      const updatedSlates = slateOverlays.filter(
                        (s) => !(s.id === preRollSlate.id && s.isPreRoll)
                      );
                      onSlateOverlaysChange(updatedSlates);
                    }
                  }}
                />
              </div>
            ) : (
              <InputWithMediaInfo
                ref={preMediaInputRef}
                label="Opening Slate"
                value={preRollMedia?.label || ""}
                handleChange={(newValue: any) => {
                  onPreRollChange?.(newValue);
                  setIsPreEdit(false);
                }}
                disabled={isPreview}
                options={preRollMediaList}
                onInputChangeCallback={(params) => {
                  setPreRollValue(params.keyword);
                  if (params.keyword.length < 3) {
                    onClearPreRollMedia?.();
                    lastProcessedPreRollValue.current = null;
                  }
                  // onPreRollSearch?.({ stitle: params.keyword, key: 'pre' });
                }}
                handleBlur={(e) => {
                  setIsPreEdit(false);
                  onClearPreRollMedia?.();
                }}
                menuPlacement="bottom"
                menuPortalTarget={document.body}
                menuStyles={{ right: 0, left: "auto" }}
                maxMenuHeight={245}
                hideArrow={true}
                className="mt-3"
                initialSelectedOption={preRollMedia}
                right="11px"
                initialStructuredData={preRollMedia}
                loader={true}
              />
            )}
          </div>

          <div className="col-12">
            {postRollSlate && !isPostEdit ? (
              <div>
                <label className="form-label mb-1">Closing Slate</label>
                <SelectedMediaCard
                  media={{
                    label: postRollSlate.id,
                    value: postRollSlate.title || "Post-Roll Slate",
                    image: postRollSlate.url,
                    duration: `${postRollSlate.duration || 5}s`,
                    thumb: !!postRollSlate.url,
                  }}
                  type="post"
                  showEditButton={true}
                  showPreviewButton={true}
                  onEdit={() => handleEditMediaCard("post")}
                  readOnlyMode={readOnlyMode}
                  disableEdit={isPreview}
                  onClose={() => {
                    if (onSlateOverlaysChange) {
                      const updatedSlates = slateOverlays.filter(
                        (s) => !(s.id === postRollSlate.id && s.isPostRoll)
                      );
                      onSlateOverlaysChange(updatedSlates);
                    }
                  }}
                />
              </div>
            ) : (
              <InputWithMediaInfo
                ref={postMediaInputRef}
                label="Closing Slate"
                value={postRollMedia?.label || ""}
                handleChange={(newValue: any) => {
                  onPostRollChange?.(newValue);
                  setIsPostEdit(false);
                }}
                options={postRollMediaList}
                onInputChangeCallback={(params) => {
                  setPostRollValue(params.keyword);
                  if (params.keyword.length < 3) {
                    onClearPostRollMedia?.();
                    lastProcessedPostRollValue.current = null;
                  }
                  // onPostRollSearch?.({ stitle: params.keyword, key: 'post' });
                }}
                handleBlur={(e) => {
                  setIsPostEdit(false);
                  onClearPostRollMedia?.();
                }}
                menuPlacement="bottom"
                menuPortalTarget={document.body}
                menuStyles={{ right: 0, left: "auto" }}
                maxMenuHeight={245}
                hideArrow={true}
                className=""
                initialSelectedOption={postRollMedia}
                right="11px"
                initialStructuredData={postRollMedia}
                loader={true}
                disabled={isPreview}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-3">
      {overlayType === "logo" && !isPreset && (
        <input
          ref={fileInputRef}
          type="file"
          className="d-none"
          accept="image/*"
          onChange={(e) => handleFileUpload(e)}
        />
      )}
      {/* <h6 className="text-uppercase">{title || defaultTitle}</h6> */}
      <div className="row gy-3 m-0 justify-content-between">
        {overlayType === "logo" && isPreset && !isPreview && (
          <ImageUpload
            registerMedia={callRegisterMediaOnCms}
            setSelectedImage={(image: File | null) => {
              setSelectedImage(image);
            }}
            callbackRef={callback}
            type="team"
            hasBaseUrl={currentMedia?.baseurl}
            setIsLoading={setIsLoading}
            handleImageUploadUrl={handleImageUploadUrl}
            isPreset={isPreset}
            isLoading={isLoading}
          />
        )}
        {showAddButton && !isPreset && !isPreview && (
          <div
            onClick={addOverlay}
            className="card catRow border p-3 pointer d-flex flex-column align-items-center justify-content-center"
            style={{ flex: "0 0 148px", height: "90px" }}
          >
            <Upload size={24} className="mb-2" />
            {addButtonText}
          </div>
        )}
        {overlayType === "logo" && overlays.length > 0 && (
          <>
            {overlays.map((overlay: BaseOverlay, index) => (
              <div
                key={overlay.id}
                className={`card border pointer position-relative ${
                  selectedLogoOverlayId === overlay.id
                    ? "border-success border-3"
                    : ""
                }`}
                style={{
                  flex: "0 0 148px",
                  height: "90px",
                  backgroundColor:
                    selectedLogoOverlayId === overlay.id
                      ? "rgba(0, 255, 0, 0.1)"
                      : "transparent",
                }}
                onClick={() => onLogoOverlaySelect?.(overlay.id)}
              >
                <img
                  src={overlay.url}
                  alt={`Logo ${index + 1}`}
                  className="img-fluid p-1 w-100 h-100"
                  style={{ objectFit: "contain" }}
                />
                {/* <div className="position-absolute bottom-0 end-0 bg-dark text-white small px-1 rounded-1 me-1 mb-1">
                  {index + 1}
                </div> */}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default UnifiedOverlayControls;
